import React, { PureComponent } from "react";
import { withTranslation } from "react-i18next";
import { cssMainSelector } from "../../../../../lib/cssSelectors";
import { getOfferResultProps } from "../../../../../lib/offerResultProps";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import MaterialIcon from "../../../../MaterialIcon";
import MonthlyFee from "../../../../MonthlyFee";
import OfferContent from "../../../../OfferContent";
import OfferTitle from "../../../../OfferTitle";
import PriceUpfront from "../../../../PriceUpfront";
import SponsoredLabel from "../../../../SponsoredLabel";
import { lighten } from "polished";
import { MODALS } from "../../../../../constants/modals";
import { CSS_CLASS_PREFIX } from "../../../../../constants/css";
import { OFFER_TYPES } from "../../../../../constants/offerTypes";
import CreditTable from "../../../../CreditTable";
import ClickOutLinkContainer from "../../../../../containers/ClickOutLinkContainer";
import ExtraOfferInfo from "../../../../ExtraOfferInfo";

const containerPadding = css`
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
`;
const Container = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    background-color: ${(p) => p.theme.offerResults.backgroundColor};
    border-radius: ${(p) => p.theme.generic.borderRadius};
    box-shadow: 0 1px 3px 0
      ${(p) =>
        p.sponsored
          ? lighten(0.3, p.theme.offerResults.sponsoredBorderColor)
          : lighten(0.2, p.theme.offerResults.borderColor)};
    margin-bottom: ${(p) => p.theme.spacing.md};
    ${containerPadding};
    position: relative;
    white-space: nowrap;
  }
`;

const Header = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding-bottom: ${(p) => p.theme.spacing.sm};
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const Rank = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    background-color: ${(p) => p.theme.color.cornFlowerBlue};
    border-radius: 17px;
    color: white;
    display: inline-block;
    font-size: 11px;
    font-weight: ${(p) => p.theme.color.weightBold};
    left: -5px;
    line-height: 17px;
    position: absolute;
    text-align: center;
    top: -5px;
    width: 17px;
    z-index: 1;
  }
`;

const Title = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: none;
    order: 0;
    white-space: normal;

    .${CSS_CLASS_PREFIX}-price-upfront {
      * {
        font-size: ${(p) => p.theme.font.sizeXs};
      }
    }
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    display: block;
    order: 1;
  }
`;

const SponsoredLabelWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    margin-left: ${(p) => p.theme.spacing.md};
    margin-right: -${(p) => p.theme.spacing.md};
    margin-top: -${(p) => p.theme.spacing.md};
    order: 1;
    padding-bottom: ${(p) => p.theme.spacing.sm};
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    margin-left: -${(p) => p.theme.spacing.md};
    margin-right: 0;
    margin-top: -${(p) => p.theme.spacing.md};
    order: 0;
  }
`;

const Offer = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const OfferTitleWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    order: 0;
    flex: 2 1 0%;
    white-space: normal;

    .${CSS_CLASS_PREFIX}-price-upfront {
      * {
        font-size: ${(p) => p.theme.font.sizeXs};
      }
    }
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    display: none;
  }
`;

const OfferContentWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    order: 0;
    flex: 3 1 0%;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    flex: 1 1 100%;
  }
`;

const CostClickOut = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex: 2 1 0%;
    justify-content: space-between;
    order: 1;
    padding-left: ${(p) => p.theme.spacing.md};
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    flex: 1 1 100%;
    padding-left: ${(p) => p.theme.spacing.none};
    padding-top: ${(p) => p.theme.spacing.md};
    order: 2;
  }
`;

const Costs = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex: 1 1 0%;
    justify-content: center;
    order: 0;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    justify-content: flex-start;
  }
`;

const MonthlyFeeWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    flex: 0 1 auto;
    order: 0;

    .${CSS_CLASS_PREFIX}-monthly-fee {
      .${CSS_CLASS_PREFIX}-currency-symbol {
        font-size: ${(p) => p.theme.font.sizeXxl};
        font-weight: ${(p) => p.theme.font.weightBold};
        letter-spacing: 1px;
      }

      .${CSS_CLASS_PREFIX}-currency-main-amount {
        font-size: ${(p) => p.theme.font.sizeXxl};
        font-weight: ${(p) => p.theme.font.weightBold};
        letter-spacing: 1px;
      }

      .${CSS_CLASS_PREFIX}-currency-trail-amount {
        font-size: ${(p) => p.theme.font.sizeXs};
        font-weight: ${(p) => p.theme.font.weightBold};
      }
    }

    .${CSS_CLASS_PREFIX}-standard-monthly-fee {
      * {
        color: ${(p) => p.theme.color.darkGray};
        font-size: ${(p) => p.theme.font.sizeXxs};
      }

      .${CSS_CLASS_PREFIX}-promotion-term-text {
        display: block;
      }
    }

    .${CSS_CLASS_PREFIX}-monthly-fee-text {
      color: ${(p) => p.theme.color.darkGray};
      font-size: ${(p) => p.theme.font.sizeXs};
    }
  }
`;

const TCOWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    flex: 0 1 auto;
    order: 1;

    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${(p) => p.theme.offerResults.infoLinkColor};
      cursor: pointer;
      padding-left: ${(p) => p.theme.spacing.sm};
      padding-top: ${(p) => p.theme.spacing.sm};
    }
  }
`;

const ClickOutWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    flex: 0 1 auto;
    order: 1;
    text-align: center;
  }
`;

const ClickOutTextShort = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: none;
    text-align: center;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    display: block;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    display: none;
  }
`;

const ClickOutTextLong = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    display: block;
    text-align: center;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "smtablet-down" })} {
    display: none;
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    display: block;
  }
`;

const CreditTableWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    margin-top: ${(p) => p.theme.spacing.sm};
  }
`;

const ExtraOfferInfoWrapper = styled.div`
  ${(p) => cssMainSelector(p.theme.tdsId)} {
    flex: 1 1 100%;
    order: 2;
    padding-top: ${(p) => p.theme.spacing.none};
  }

  ${(p) => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    order: 1;
  }
`;

class OfferResult extends PureComponent {
  render() {
    const {
      clientConfig,
      portal,
      portalOfferResult,
      offerType,
      rank,
      t,
      updateModal,
      widgetOptions,
    } = this.props;
    const oRP = getOfferResultProps(portalOfferResult);

    return (
      <Container sponsored={oRP.isSponsored} data-offer-id={oRP.id}>
        {widgetOptions.withRanks && rank && <Rank>{rank}</Rank>}
        {(oRP.isSponsored || offerType !== OFFER_TYPES.simOnly) && (
          <Header>
            <Title>
              <OfferTitle
                brand={oRP.brand}
                phone={oRP.phone}
                phoneState={oRP.phoneState}
                provider={oRP.provider}
                showProvider={false}
                titleSource={oRP.titleSource}
              />{" "}
              {offerType !== OFFER_TYPES.simOnly &&
                clientConfig.widgetOptions[offerType].showPrice && (
                  <PriceUpfront amount={oRP.price} portal={portal} />
                )}
            </Title>
            <SponsoredLabelWrapper>
              {oRP.isSponsored && <SponsoredLabel />}
            </SponsoredLabelWrapper>
          </Header>
        )}

        <Offer>
          {offerType !== OFFER_TYPES.simOnly && (
            <OfferTitleWrapper>
              <OfferTitle
                brand={oRP.brand}
                phone={oRP.phone}
                phoneState={oRP.phoneState}
                provider={oRP.provider}
                showProvider={false}
                titleSource={oRP.titleSource}
              />{" "}
              {offerType !== OFFER_TYPES.simOnly &&
                clientConfig.widgetOptions[offerType].showPrice && (
                  <PriceUpfront amount={oRP.price} portal={portal} />
                )}
            </OfferTitleWrapper>
          )}
          <OfferContentWrapper>
            <OfferContent
              clientConfig={clientConfig}
              {...oRP}
              portal={portal}
            />
          </OfferContentWrapper>
          <CostClickOut>
            <Costs>
              <MonthlyFeeWrapper>
                <MonthlyFee
                  contractTermInMonths={oRP.contractTermInMonths}
                  monthlyFee={oRP.monthlyFee}
                  portal={portal}
                  promotionTerm={oRP.promotionTerm}
                  standardMonthlyFee={oRP.standardMonthlyFee}
                  short={true}
                />
              </MonthlyFeeWrapper>
              <TCOWrapper
                onClick={updateModal.bind(this, oRP, MODALS.tcoModal)}
              >
                <MaterialIcon>info_outlined</MaterialIcon>
              </TCOWrapper>
            </Costs>
            <ClickOutWrapper>
              <ClickOutTextShort>
                <ClickOutLinkContainer
                  deepLink={oRP.deepLink}
                  type={"button"}
                >
                  {t("shared.showMeShort")}
                </ClickOutLinkContainer>
              </ClickOutTextShort>
              <ClickOutTextLong>
                <ClickOutLinkContainer
                  deepLink={oRP.deepLink}
                  type={"button"}
                >
                  {t("shared.showMe")}
                </ClickOutLinkContainer>
              </ClickOutTextLong>
            </ClickOutWrapper>
          </CostClickOut>
          <ExtraOfferInfoWrapper>
            <ExtraOfferInfo
              clientConfig={clientConfig}
              offerType={offerType}
              offerResultProps={oRP}
              portal={portal}
            />
          </ExtraOfferInfoWrapper>
        </Offer>
        {oRP.isSponsored &&
          offerType === OFFER_TYPES.proposition &&
          oRP.showCreditTable && (
            <CreditTableWrapper>
              <CreditTable oRP={oRP} portal={portal} />
            </CreditTableWrapper>
          )}
      </Container>
    );
  }
}

OfferResult.propTypes = {
  clientConfig: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  portal: PropTypes.object,
  portalOfferResult: PropTypes.object.isRequired,
  updateModal: PropTypes.func.isRequired,
  widgetOptions: PropTypes.object,
};

export default withTranslation(["topList"])(OfferResult);
